/**
 * DOM Utilities - Safe alternatives to innerHTML
 * Prevents XSS by using DOM APIs instead of string interpolation
 */

/**
 * Escape HTML entities in a string
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
}

/**
 * Create an element with text content (safe from XSS)
 * @param {string} tag - HTML tag name
 * @param {string} text - Text content (will be escaped)
 * @param {Object} attrs - Attributes to set
 * @returns {HTMLElement}
 */
export function createElement(tag, text = '', attrs = {}) {
    const el = document.createElement(tag);
    if (text) {
        el.textContent = text;
    }
    for (const [key, value] of Object.entries(attrs)) {
        if (key === 'className') {
            el.className = value;
        } else if (key === 'dataset') {
            for (const [dataKey, dataValue] of Object.entries(value)) {
                el.dataset[dataKey] = dataValue;
            }
        } else if (key.startsWith('on')) {
            // Skip inline event handlers - use addEventListener instead
            continue;
        } else {
            el.setAttribute(key, value);
        }
    }
    return el;
}

/**
 * Safely set text content of an element
 * @param {HTMLElement|string} el - Element or selector
 * @param {string} text - Text to set
 */
export function setText(el, text) {
    const element = typeof el === 'string' ? document.querySelector(el) : el;
    if (element) {
        element.textContent = text;
    }
}

/**
 * Clear all children from an element
 * @param {HTMLElement} el - Element to clear
 */
export function clearChildren(el) {
    while (el.firstChild) {
        el.removeChild(el.firstChild);
    }
}

/**
 * Create hazard alert element safely
 * @param {string} hazardText - Hazard description (untrusted)
 * @returns {HTMLElement}
 */
export function createHazardAlert(hazardText) {
    const div = document.createElement('div');
    div.className = 'hazard-alert';

    const icon = document.createElement('span');
    icon.textContent = '\u26A0'; // Warning symbol

    const text = document.createElement('span');
    text.textContent = hazardText; // Safe - uses textContent

    div.appendChild(icon);
    div.appendChild(document.createTextNode(' '));
    div.appendChild(text);

    return div;
}

/**
 * Create breakdown factor element safely
 * @param {Object} factor - Factor data
 * @returns {HTMLElement}
 */
export function createBreakdownFactor(factor) {
    const div = document.createElement('div');
    div.className = 'breakdown-factor';

    const nameSpan = createElement('span', factor.factor, { className: 'factor-name' });
    const detailSpan = createElement('span', factor.detail, { className: 'factor-detail' });

    const impactClass = typeof factor.impact === 'string' && factor.impact.startsWith('+')
        ? 'positive'
        : (factor.impact === '\u2014' || factor.impact === 0) ? 'neutral' : 'negative';
    const impactSpan = createElement('span', String(factor.impact), { className: `factor-impact ${impactClass}` });

    div.appendChild(nameSpan);
    div.appendChild(detailSpan);
    div.appendChild(impactSpan);

    return div;
}

/**
 * Create error state element safely
 * @param {string} message - Error message
 * @param {Function} retryHandler - Retry callback (optional)
 * @returns {HTMLElement}
 */
export function createErrorState(message, retryHandler = null) {
    const div = document.createElement('div');
    div.className = 'error-state';

    const icon = createElement('span', '\u26A0\uFE0F', { className: 'error-icon' });
    const messageSpan = createElement('span', message, { className: 'error-message' });

    div.appendChild(icon);
    div.appendChild(messageSpan);

    if (retryHandler) {
        const btn = createElement('button', 'Retry', { className: 'retry-btn' });
        btn.addEventListener('click', retryHandler);
        div.appendChild(btn);
    }

    return div;
}

/**
 * Create a search input element safely
 * @param {string} id - Input ID
 * @param {string} placeholder - Placeholder text
 * @param {string} value - Initial value
 * @returns {HTMLInputElement}
 */
export function createSearchInput(id, placeholder, value = '') {
    const input = document.createElement('input');
    input.type = 'search';
    input.setAttribute('aria-label', placeholder);
    input.id = id;
    input.className = 'spot-search';
    input.placeholder = placeholder;
    input.value = value;
    return input;
}

/**
 * Create fatal error display safely
 * @param {Error} error - Error object
 * @returns {HTMLElement}
 */
export function createFatalErrorDisplay(error) {
    const container = document.createElement('div');
    container.className = 'error-fullscreen';

    const h1 = createElement('h1', 'Failed to load dashboard');
    const p = createElement('p', error.message);
    const btn = createElement('button', 'Retry');
    btn.addEventListener('click', () => location.reload());

    container.appendChild(h1);
    container.appendChild(p);
    container.appendChild(btn);

    return container;
}

/**
 * Create SVG element with child elements
 * @param {number} width - SVG width
 * @param {number} height - SVG height
 * @param {string} viewBox - viewBox attribute
 * @param {Array<Object>} children - Array of child element configs {tag, ...attrs}
 * @param {Object} attrs - Additional SVG attributes (fill, stroke, etc.)
 * @returns {SVGElement}
 */
export function createSvg(width, height, viewBox, children, attrs = {}) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    svg.setAttribute('viewBox', viewBox);

    for (const [key, value] of Object.entries(attrs)) {
        svg.setAttribute(key, value);
    }

    for (const childData of children) {
        const tagName = childData.tag || 'path';
        const child = document.createElementNS('http://www.w3.org/2000/svg', tagName);
        for (const [attr, val] of Object.entries(childData)) {
            if (attr !== 'tag') {
                child.setAttribute(attr, val);
            }
        }
        svg.appendChild(child);
    }

    return svg;
}

/**
 * Populate a <select> element from an object dictionary
 * Used for region/area/station dropdowns
 *
 * @param {HTMLSelectElement} select - The select element
 * @param {Object} items - Dictionary of {id: {name: string, ...}}
 * @param {string|null} selectedId - ID to mark as selected (optional)
 * @param {string|null} placeholder - Placeholder text (optional)
 */
export function populateSelect(select, items, selectedId = null, placeholder = null) {
    if (!select) return;

    // Clear existing options
    select.innerHTML = '';

    // Add placeholder if provided
    if (placeholder) {
        const placeholderOpt = document.createElement('option');
        placeholderOpt.value = '';
        placeholderOpt.textContent = placeholder;
        placeholderOpt.disabled = true;
        if (!selectedId) placeholderOpt.selected = true;
        select.appendChild(placeholderOpt);
    }

    // Add options from items dictionary
    for (const [id, item] of Object.entries(items || {})) {
        const option = document.createElement('option');
        option.value = id;
        option.textContent = item.name;
        if (id === selectedId) option.selected = true;
        select.appendChild(option);
    }
}
